/**
 * SCRUM-1032 round 7, item 9 — what an ET Reference month may hold.
 *
 * The range is the one the rest of the app already uses: the sidebar form
 * accepts 0–65 mm per month, and the backend's Excel import rejects anything
 * from 65.54 up, since the controller cannot store it. The grid used to accept
 * anything: -5 was clamped to 0 by the editor, "abc" became 0 in the payload,
 * and 9999 went to the server.
 *
 * Values are checked in mm. The grid converts what is typed to mm at the point
 * it is staged, so an imperial user's inches are already metric here.
 */
export const ET_MONTH_MIN_MM = 0
export const ET_MONTH_MAX_MM = 65

/**
 * Slack at the top of the range, in mm. An imperial user is told the maximum
 * is 2.56 in, which converts back to 65.02 mm: without this the stated maximum
 * was itself refused. Well inside the 65.54 the backend accepts.
 */
export const ET_MONTH_TOLERANCE_MM = 0.05

/**
 * Whether a staged month value can be saved.
 *
 * Blank (undefined, null, '') is valid: a month left empty is saved as 0, as
 * it always has been. Anything else must be a finite number within range.
 */
export const validateEtMonth = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true
  const number = typeof value === 'number' ? value : Number(value)
  return (
    Number.isFinite(number) &&
    number >= ET_MONTH_MIN_MM &&
    number <= ET_MONTH_MAX_MM + ET_MONTH_TOLERANCE_MM
  )
}

/**
 * The months in a staged set of values that cannot be saved, in the order
 * given. Only months present in `values` are looked at, so an edit that did
 * not touch a month is never refused for it.
 */
export const invalidEtMonths = <M extends string>(
  values: Partial<Record<string, unknown>>,
  months: readonly M[],
): M[] =>
  months.filter((month) => month in values && !validateEtMonth(values[month]))
